import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface LocationDetails {
  state: string;
  country: string;
  displayName: string;
}

interface LocationLookupProps {
  onLocationFound: (location: string) => void;
  defaultLocation?: string;
}

export function LocationLookup({
  onLocationFound,
  defaultLocation,
}: LocationLookupProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [location, setLocation] = useState(defaultLocation || '');

  // Set default location on mount if provided
  useEffect(() => {
    if (defaultLocation) {
      setLocation(defaultLocation);
      onLocationFound(defaultLocation);
    }
    // Only run on mount or when defaultLocation changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultLocation]);

  const handleLocationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newLocation = e.target.value;
    setLocation(newLocation);
    onLocationFound(newLocation);
  };

  const handleGetLocation = () => {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by your browser');
      return;
    }

    setIsLoading(true);
    setError('');

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { latitude, longitude } = position.coords;

          // Use Nominatim API to reverse geocode
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=10&addressdetails=1`,
            {
              headers: {
                'Accept-Language': 'en-US,en;q=0.9',
                'User-Agent': 'ValuationApp/1.0',
              },
            },
          );

          if (!response.ok) {
            throw new Error('Failed to fetch location details');
          }

          const data = await response.json();
          const locationDetails: LocationDetails = {
            state: data.address.state || data.address.county || '',
            country: data.address.country || '',
            displayName: data.display_name || '',
          };

          // Format the location string
          const locationString = [
            locationDetails.state,
            locationDetails.country,
          ]
            .filter(Boolean)
            .join(', ');

          setLocation(locationString);
          onLocationFound(locationString);
        } catch (err) {
          setError(
            err instanceof Error
              ? err.message
              : 'Failed to get location details',
          );
        } finally {
          setIsLoading(false);
        }
      },
      () => {
        setError('Unable to retrieve your location');
        setIsLoading(false);
      },
    );
  };

  return (
    <div className='flex flex-col space-y-1.5'>
      <Label htmlFor='location'>Location</Label>
      <div className='flex flex-col gap-2 sm:flex-row'>
        <Input
          id='location'
          placeholder='State/Region, Country or Worldwide'
          value={location}
          onChange={handleLocationChange}
          required
        />
        <Button
          type='button'
          variant='outline'
          onClick={handleGetLocation}
          disabled={isLoading}
        >
          {isLoading ? 'Getting...' : 'Get Location'}
        </Button>
      </div>
      {error && <p className='text-sm text-destructive'>{error}</p>}
    </div>
  );
}
